import Drawer from '@mui/material/Drawer'
import {FC, useCallback, useEffect, useMemo, useState} from 'react'
import {useDispatch} from 'react-redux'
import {useLocation, useNavigate} from 'react-router-dom'
import {
  ChatRequest,
  clearNewPersonalStack,
  deleteToPersonalStack,
  Header,
  NotificationType,
  NotificationTypes,
  PersonalStackType,
  TextButton,
  usePersonalNotificationList,
  useTopNotification
} from 'entities/activity'
import {ContentLastMessage, DialogItemType} from 'entities/messages'
import {useSound} from 'entities/user'
import {mediaVariants} from 'shared/lib/media'
import {playAudio} from 'shared/lib/sound'
import {Avatar} from 'shared/ui/avatar'
import {Button} from 'shared/ui/buttons'
import {Hot, Verified} from 'shared/ui/icons'
import styles from './styles.module.scss'
import './drawer.scss'

export const PersonalNotification = () => {
  const dispatch = useDispatch()
  const location = useLocation()
  const personalNotification = usePersonalNotificationList()
  const topNotification = useTopNotification()
  const [isOpen, setIsOpen] = useState(false)

  useEffect(() => {
    if (!location.pathname.includes('messages')) return

    const dialogs = personalNotification.filter((item) => item.type === 'dialog')

    if (!dialogs.length) return

    for (const dialog of dialogs) {
      dispatch(deleteToPersonalStack(dialog.id))
    }
  }, [location.pathname, personalNotification])

  useEffect(() => {
    if (topNotification || !(location.pathname.includes('people') ||
      location.pathname.includes('favorites') ||
      location.pathname.includes('myProfile'))) {
      return setIsOpen(false)
    }

    setIsOpen(true)
    dispatch(clearNewPersonalStack())
  }, [location.pathname, topNotification])

  return (
    <Drawer
      open={isOpen}
      anchor='bottom'
      classes={{root: 'mobilePersonalNotificationRoot'}}
    >
      <div className={styles.notificationsList}>
        {personalNotification.map((item) => <Item key={item.id} personalStack={item} />)}
      </div>
    </Drawer>
  )
}

const Item: FC<{personalStack: PersonalStackType}> = ({personalStack}) => {
  const isSound = useSound()
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const user = useMemo(() => {
    if (personalStack.type === 'notification') {
      return (personalStack.item as NotificationType).from
    }
    if (personalStack.type === 'dialog') {
      return (personalStack.item as DialogItemType).interlocutor
    }

    return (personalStack.item as ChatRequest).userFrom
  }, [personalStack])

  const content = useMemo(() => {
    if (personalStack.type === 'notification') {
      return <span>{Header[(personalStack.item as NotificationType).type]}</span>
    }
    if (personalStack.type === 'dialog') {
      return <ContentLastMessage dialog={personalStack.item as DialogItemType} />
    }

    return <span>{(personalStack.item as ChatRequest).content.message}</span>
  }, [personalStack])

  const buttonText = useMemo(() => {
    if (personalStack.type === 'notification') {
      return TextButton[(personalStack.item as NotificationType).type]
    }

    return 'Reply'
  }, [personalStack])

  const openClickHandler = useCallback(() => {
    dispatch(deleteToPersonalStack(personalStack.id))

    if (personalStack.type === 'notification') {
      const notification = personalStack.item as NotificationType

      if (notification.type === NotificationTypes.LIKE || notification.type === NotificationTypes.FAVORITE) {
        return navigate(`/profile/${notification.from.id}`)
      }

      return navigate(`/messages/${notification.from.id}`)
    }

    if (personalStack.type === 'dialog') {
      return navigate(`/messages/${(personalStack.item as DialogItemType).interlocutor.id}`)
    }

    return navigate(`/messages/${(personalStack.item as ChatRequest).userFrom.id}`)
  }, [navigate, personalStack])

  useEffect(() => {
    personalStack.isNew && isSound && playAudio()
  }, [])

  return (
    <div className={`${styles.notification} ${personalStack.isNew ? styles.new : ''}`} onClick={openClickHandler}>
      <Avatar
        src={user.avatarUrl + mediaVariants.square64}
        alt={user.name}
        isOnline={user.isOnline}
        sx={{width: 52, height: 52}}
      />
      <div className={styles.info}>
        <div className={styles.name}>
          <span>{user.name}, {user.age}</span>
          {user.isConfirmed ? <div className={styles.verifiedIcon}><Verified /></div> : null}
          {user.isPopular ? <div className={styles.hotIcon}><Hot /></div> : null}
        </div>
        <div className={styles.text}>{content}</div>
      </div>
      <Button
        styleType='primary'
        className={`${styles.replyButton} ${personalStack.type === 'notification' ? styles.green : ''}`}
      >
        <div className={`${styles.back} ${personalStack.isNew ? styles.animate : ''}`} />
        <span>{buttonText}</span>
      </Button>
    </div>
  )
}